/** Shared API domain types mirroring the backend contracts. */

export enum UserRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export enum ProjectRole {
  OWNER = 'OWNER',
  EDITOR = 'EDITOR',
  REVIEWER = 'REVIEWER',
  VIEWER = 'VIEWER',
}

export enum TranslationStatus {
  PENDING = 'PENDING',
  MACHINE_TRANSLATED = 'MACHINE_TRANSLATED',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum TranslationSource {
  HUMAN = 'HUMAN',
  AI = 'AI',
  IMPORT = 'IMPORT',
}

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole | string;
  name?: string;
}

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  user: AuthUser;
}

export interface Language {
  id: string;
  code: string;
  name: string;
  nativeName?: string;
  isRtl?: boolean;
  isActive?: boolean;
}

export interface ProjectMember {
  user: string;
  role: ProjectRole;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  description?: string;
  owner: string;
  sourceLanguage: string;
  targetLanguages: string[];
  members: ProjectMember[];
  createdAt?: string;
  updatedAt?: string;
}

export interface TranslationKey {
  id: string;
  project: string;
  key: string;
  sourceText: string;
  description?: string;
  context?: string;
  tags: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Translation {
  id: string;
  project: string;
  translationKey: string | { id: string; key: string; sourceText: string };
  language: string;
  value: string;
  status: TranslationStatus;
  source: TranslationSource;
  version: number;
  reviewNote?: string;
  updatedAt?: string;
}

export interface TranslationVersion {
  id: string;
  language: string;
  value: string;
  status: TranslationStatus;
  source: TranslationSource;
  version: number;
  createdAt?: string;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt?: string;
  /** Only present in the create response. */
  key?: string;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export enum JobType {
  TRANSLATE_MISSING = 'TRANSLATE_MISSING',
}

export enum JobStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface TranslationJob {
  id: string;
  project: string;
  type: JobType | string;
  status: JobStatus;
  languages: string[];
  total: number;
  processed: number;
  translated: number;
  skipped: number;
  failed: number;
  error?: string;
  startedAt?: string;
  finishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}
